import { useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { categories, categoriesMap, getComicsByCategory } from '@/mocks/comics';

type SortKey = 'new' | 'hot' | 'rating';

const parseViews = (v: string) => {
  const n = parseFloat(v);
  return v.includes('万') ? n * 10000 : n;
};

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const cat = categoriesMap[slug];
  const [sort, setSort] = useState<SortKey>('new');

  const list = useMemo(() => {
    const base = getComicsByCategory(slug);
    if (sort === 'rating') return [...base].sort((a, b) => b.rating - a.rating);
    if (sort === 'hot') return [...base].sort((a, b) => parseViews(b.views) - parseViews(a.views));
    return base;
  }, [slug, sort]);

  if (!cat) {
    return <Navigate to="/category/shenye" replace />;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${cat.name}漫画 - 嘿嘿漫画网`,
    description: cat.description,
    url: `https://dailycartoon.github.io/category/${cat.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dailycartoon.github.io/' },
        { '@type': 'ListItem', position: 2, name: cat.name },
      ],
    },
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <Header />

      <main>
        <section className="relative overflow-hidden border-b border-background-200/60">
          <div className="absolute inset-0">
            <img src={cat.banner} alt={`${cat.name}漫画专区背景`} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40"></div>
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <nav aria-label="面包屑" className="mb-4 text-xs text-foreground-400">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="transition-colors hover:text-primary-400">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
                <li className="text-foreground-300">{cat.name}漫画</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">{cat.name}漫画专区</h1>
            <p className="mt-1 font-heading text-lg text-accent-400">{cat.en}</p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-300">{cat.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {cat.tags.map((t) => (
                <span key={t} className="rounded-full border border-foreground-400/30 px-3 py-1 text-xs text-foreground-300">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold text-foreground-950">共 {list.length} 部作品</h2>
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {[
                { key: 'new' as SortKey, label: '最新更新' },
                { key: 'hot' as SortKey, label: '人气最高' },
                { key: 'rating' as SortKey, label: '评分最高' },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSort(s.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === s.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-secondary-100 hover:text-foreground-950'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div data-product-shop className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}